/* ==========================================================================
   Levendi — PDP GALLERY behaviour
   Worker `levendi-pdp-gallery`, 10 Sep 2026.

   The theme owns the gallery. This file adds only what the theme does not do,
   and nothing it adds is load-bearing — with this script blocked the gallery
   still works exactly as Horizon ships it.

   What it adds:
     1. Arrow-key navigation across the thumbnail rail (and the lightbox's own
        thumbnail list). The theme renders the thumbs as <button>s, so Tab and
        Enter already work; a rail of ten pieces should not need ten tabs.
        Up/Down (and Left/Right, so the horizontal mobile list behaves too)
        move a roving focus, Home/End jump the ends, and the moved-to thumb is
        activated, which is what selects the slide.
     2. Keeps the focused thumb inside the scrolling rail.
     3. Locks the page behind the open lightbox. The theme HAS a scroll lock —
        html[scroll-lock] { overflow: hidden } in assets/base.css — but the only
        thing that sets it is a `toggle` listener for <details> elements
        (assets/dialog.js). The zoom dialog carries the `scroll-lock` attribute
        and never gets the attribute set, so the page scrolls away behind an
        open lightbox: measured at 1440, the document moved a full 598px under
        it. This wires the dialog to the lock the theme already owns.

   No third-party library, no dependencies, no module imports — this is a plain
   deferred script so it can be dropped without touching the theme's bundler.
   ========================================================================== */
(function () {
  'use strict';

  var RAILS = '.slideshow-controls__thumbnails, .dialog-thumbnails-list';
  var THUMB = '.slideshow-controls__thumbnail, .dialog-thumbnails-list__thumbnail';
  var KEYS = {
    ArrowUp: -1,
    ArrowLeft: -1,
    ArrowDown: 1,
    ArrowRight: 1,
  };

  function thumbsIn(rail) {
    return Array.prototype.filter.call(rail.querySelectorAll(THUMB), function (el) {
      return el.offsetParent !== null || el.getClientRects().length > 0;
    });
  }

  function focusThumb(thumb, rail) {
    if (!thumb) return;
    // Move focus first, then activate: activating scrolls the slideshow, and a
    // focus() afterwards can fight that scroll in Safari.
    try {
      thumb.focus({ preventScroll: true });
    } catch (e) {
      thumb.focus();
    }
    if (rail && typeof thumb.scrollIntoView === 'function') {
      var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      thumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: reduced ? 'instant' : 'smooth' });
    }
    thumb.click();
  }

  function onKeydown(event) {
    if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;

    var key = event.key;
    if (!(key in KEYS) && key !== 'Home' && key !== 'End') return;

    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    var current = target.closest(THUMB);
    if (!current) return;

    var rail = current.closest(RAILS);
    if (!rail) return;

    var thumbs = thumbsIn(rail);
    if (thumbs.length < 2) return;

    var index = thumbs.indexOf(current);
    if (index === -1) return;

    var next;
    if (key === 'Home') {
      next = 0;
    } else if (key === 'End') {
      next = thumbs.length - 1;
    } else {
      next = index + KEYS[key];
      // Deliberately clamped, not wrapped: a rail that jumps from the last
      // piece back to the first reads as a glitch, not as a loop.
      if (next < 0 || next > thumbs.length - 1) {
        event.preventDefault();
        return;
      }
    }

    event.preventDefault();
    focusThumb(thumbs[next], rail);
  }

  /* ---- 3. body scroll lock behind the lightbox -------------------------- */

  function lockFor(dialog) {
    if (!dialog || dialog.dataset.lvScrollLock === '1') return;
    dialog.dataset.lvScrollLock = '1';
    if (typeof MutationObserver !== 'function') return;

    var apply = function () {
      // Any open lightbox anywhere on the page holds the lock.
      var anyOpen = document.querySelector('media-gallery zoom-dialog dialog[open]');
      if (anyOpen) {
        document.documentElement.setAttribute('scroll-lock', '');
      } else {
        document.documentElement.removeAttribute('scroll-lock');
      }
    };

    new MutationObserver(apply).observe(dialog, { attributes: true, attributeFilter: ['open'] });
    // The dialog can also be dismissed by the platform (ESC, form method=dialog),
    // which fires `close` without necessarily running the theme's own handler.
    dialog.addEventListener('close', apply);
    dialog.addEventListener('cancel', function () {
      setTimeout(apply, 0);
    });
    apply();
  }

  function wire(gallery) {
    if (!gallery || gallery.dataset.lvPdpGallery === '1') return;
    gallery.dataset.lvPdpGallery = '1';
    gallery.addEventListener('keydown', onKeydown);
    Array.prototype.forEach.call(gallery.querySelectorAll('zoom-dialog dialog'), lockFor);
  }

  function wireAll(root) {
    var scope = root && typeof root.querySelectorAll === 'function' ? root : document;
    Array.prototype.forEach.call(scope.querySelectorAll('media-gallery'), wire);
    if (scope !== document && scope.matches && scope.matches('media-gallery')) wire(scope);
  }

  function start() {
    wireAll(document);

    // The gallery is re-rendered on variant change (section rendering API), so
    // watch for replacements rather than assuming one pass is enough. The
    // data-attribute guard above makes re-wiring a no-op.
    if (typeof MutationObserver !== 'function') return;
    var observer = new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          if (added[j].nodeType === 1) wireAll(added[j]);
        }
      }
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
